import { useState, useEffect, useCallback } from "react";
import Layout from "@/components/Layout";
import { BarChart3, TrendingUp, Users, CreditCard, ArrowUpRight, ArrowDownRight, Package, Dumbbell, Apple, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

function MetricCard({ icon: Icon, label, value, sub, trend, color = "text-slate-900", bg = "bg-white" }) {
  return (
    <div className={`${bg} rounded-xl border border-slate-200 p-4 flex flex-col gap-1`} data-testid={`metric-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center justify-between">
        <Icon className={`w-4 h-4 ${color}`} />
        {trend !== undefined && (
          <span className={`flex items-center gap-0.5 text-[10px] font-bold ${trend >= 0 ? 'text-emerald-600' : 'text-rose-600'}`}>
            {trend >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {Math.abs(trend)}%
          </span>
        )}
      </div>
      <p className="text-2xl font-black text-slate-900 mt-1">{value}</p>
      <p className="text-[10px] font-semibold text-slate-400 uppercase">{label}</p>
      {sub && <p className="text-[10px] text-slate-400">{sub}</p>}
    </div>
  );
}

function MiniBar({ data, maxVal }) {
  const h = maxVal > 0 ? (data.revenue / maxVal) * 60 : 0;
  return (
    <div className="flex flex-col items-center gap-1 flex-1 min-w-0">
      <div className="w-full flex justify-center">
        <div className="w-5 bg-slate-100 rounded-t relative" style={{ height: 60 }}>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-violet-600 to-violet-400 rounded-t transition-all duration-500" style={{ height: Math.max(h, 2) }} />
        </div>
      </div>
      <span className="text-[8px] text-slate-400 font-medium truncate w-full text-center">{data.label.split(" ")[0]}</span>
    </div>
  );
}

function DonutChart({ data, size = 120 }) {
  const total = data.reduce((s, d) => s + d.value, 0);
  if (total === 0) {
    return (
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r="40" fill="none" stroke="#e2e8f0" strokeWidth="14" />
        <text x="50" y="54" textAnchor="middle" className="text-[11px] font-bold" fill="#94a3b8">0</text>
      </svg>
    );
  }
  const c = 2 * Math.PI * 40;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox="0 0 100 100">
      {data.filter(d => d.value > 0).map((d, i) => {
        const len = (d.value / total) * c;
        const el = (
          <circle key={i} cx="50" cy="50" r="40" fill="none" stroke={d.color} strokeWidth="14"
            strokeDasharray={`${len} ${c - len}`} strokeDashoffset={`${-offset}`} transform="rotate(-90 50 50)" />
        );
        offset += len;
        return el;
      })}
      <text x="50" y="50" textAnchor="middle" className="text-[14px] font-black" fill="#0f172a">{total}</text>
      <text x="50" y="62" textAnchor="middle" className="text-[7px]" fill="#94a3b8">Nutzer</text>
    </svg>
  );
}

export default function RevenueDashboardPage({ onLogout }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDashboard = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/revenue/dashboard`, { headers: getHeaders() });
      setData(res.data);
    } catch (err) {
      if (err?.response?.status === 403) {
        setError("Zugriff verweigert — nur für Administratoren.");
      } else {
        setError("Fehler beim Laden der Dashboard-Daten.");
      }
      toast.error("Dashboard konnte nicht geladen werden");
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  if (loading) return <Layout onLogout={onLogout}><div className="flex items-center justify-center h-full"><RefreshCw className="w-6 h-6 animate-spin text-slate-400" /></div></Layout>;
  if (error) return <Layout onLogout={onLogout}><div className="flex items-center justify-center h-full text-slate-500">{error}</div></Layout>;
  if (!data) return null;

  const { summary, clients, content, monthly_revenue, plan_distribution, recent_payments } = data;
  const maxRevenue = Math.max(...monthly_revenue.map(m => m.revenue), 1);

  return (
    <Layout onLogout={onLogout}>
      <div className="flex-1 overflow-y-auto">
        <div className="p-6 w-full space-y-5 pb-12" data-testid="revenue-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-black text-slate-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-violet-500" /> Revenue Dashboard
            </h1>
            <p className="text-xs text-slate-400 mt-0.5">Geschäftskennzahlen und Umsatzanalyse</p>
          </div>
          <button onClick={loadDashboard} className="text-slate-400 hover:text-slate-600 p-2 rounded-lg hover:bg-slate-100">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>

        {/* KPI Cards - Row 1 */}
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
          <MetricCard icon={CreditCard} label="Gesamtumsatz" value={`€${summary.total_revenue.toFixed(0)}`} sub={summary.total_refunded > 0 ? `€${summary.total_refunded} erstattet` : "Brutto"} color="text-emerald-600" />
          <MetricCard icon={CreditCard} label="MRR" value={`€${summary.mrr.toFixed(0)}`} sub="Monthly Recurring" color="text-violet-600" />
          <MetricCard icon={TrendingUp} label="ARR" value={`€${summary.arr.toFixed(0)}`} sub="Annual Recurring" color="text-blue-600" />
          <MetricCard icon={Users} label="Aktive Abos" value={summary.active_subscribers} sub={`${summary.basic_count} Basic • ${summary.premium_count} Premium`} color="text-sky-600" />
          <MetricCard icon={Users} label="Churn Rate" value={`${summary.churn_rate}%`} sub={`${summary.total_cancelled} gekündigt`} color="text-rose-600" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Revenue Chart */}
          <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-bold text-slate-700 mb-4">Monatlicher Umsatz (12 Monate)</h2>
            <div className="flex items-end gap-1 h-[100px]">
              {monthly_revenue.map((m, i) => (
                <MiniBar key={i} data={m} maxVal={maxRevenue} />
              ))}
            </div>
            <div className="flex justify-between mt-3 text-[9px] text-slate-400">
              <span>{monthly_revenue[0]?.label}</span>
              <span>{monthly_revenue[monthly_revenue.length - 1]?.label}</span>
            </div>
            {/* Revenue numbers */}
            <div className="grid grid-cols-4 gap-2 mt-4 pt-3 border-t border-slate-100">
              {monthly_revenue.slice(-4).map((m, i) => (
                <div key={i} className="text-center">
                  <p className="text-xs font-bold text-slate-700">€{m.revenue.toFixed(0)}</p>
                  <p className="text-[9px] text-slate-400">{m.label.split(" ")[0]}</p>
                  <p className="text-[9px] text-emerald-500">+{m.new_total} Abos</p>
                </div>
              ))}
            </div>
          </div>

          {/* Plan Distribution */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h2 className="text-sm font-bold text-slate-700 mb-4">Planverteilung</h2>
            <div className="flex justify-center">
              <DonutChart data={plan_distribution} size={140} />
            </div>
            <div className="mt-4 space-y-2">
              {plan_distribution.map((d, i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: d.color }} />
                    <span className="text-xs font-medium text-slate-600">{d.name}</span>
                  </div>
                  <span className="text-xs font-bold text-slate-800">{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Business Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard icon={Users} label="Registrierte Nutzer" value={summary.total_users} sub={`+${summary.recent_signups} in 30 Tagen`} color="text-slate-600" />
          <MetricCard icon={Users} label="CRM-Kunden" value={clients.crm} sub={`+ ${clients.bio} Bioimpedanz`} color="text-sky-600" />
          <MetricCard icon={Apple} label="Ernährungspläne" value={content.nutrition_plans} sub={`${content.foods} Lebensmittel`} color="text-emerald-600" />
          <MetricCard icon={Dumbbell} label="Trainingspläne" value={content.training_plans} sub={`${content.exercises} Übungen`} color="text-amber-600" />
        </div>

        {/* Recent Revenue Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <div className="px-5 py-3 bg-slate-900 text-white flex items-center justify-between">
            <span className="text-xs font-bold">Monatliche Details</span>
            <span className="text-[10px] text-slate-300">Letzte 6 Monate</span>
          </div>
          <table className="w-full text-xs">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-2 font-semibold text-slate-600">Monat</th>
                <th className="text-center px-3 py-2 font-semibold text-violet-600">Umsatz</th>
                <th className="text-center px-3 py-2 font-semibold text-blue-600">Neue Basic</th>
                <th className="text-center px-3 py-2 font-semibold text-violet-600">Neue Premium</th>
                <th className="text-center px-3 py-2 font-semibold text-slate-600">Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {monthly_revenue.slice(-6).reverse().map((m, i) => (
                <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                  <td className="px-4 py-2 font-medium text-slate-800">{m.label}</td>
                  <td className="text-center px-3 py-2 font-bold text-violet-700">€{m.revenue.toFixed(0)}</td>
                  <td className="text-center px-3 py-2 text-blue-600">{m.new_basic}</td>
                  <td className="text-center px-3 py-2 text-violet-600">{m.new_premium}</td>
                  <td className="text-center px-3 py-2 font-semibold">{m.new_total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Recent Stripe Payments */}
        {recent_payments && recent_payments.length > 0 && (
          <div className="bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="recent-payments">
            <div className="px-5 py-3 bg-gradient-to-r from-emerald-600 to-teal-600 text-white flex items-center justify-between">
              <span className="text-xs font-bold">Letzte Stripe-Zahlungen</span>
              <span className="text-[10px] text-emerald-100">{summary.total_stripe_events || 0} Webhook-Events</span>
            </div>
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="text-left px-4 py-2 font-semibold text-slate-600">E-Mail</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600">Plan</th>
                  <th className="text-center px-3 py-2 font-semibold text-slate-600">Zahlung</th>
                  <th className="text-center px-3 py-2 font-semibold text-emerald-600">Betrag</th>
                  <th className="text-left px-3 py-2 font-semibold text-slate-600">Datum</th>
                </tr>
              </thead>
              <tbody>
                {recent_payments.map((p, i) => (
                  <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                    <td className="px-4 py-2 font-medium text-slate-800">{p.email || "\u2014"}</td>
                    <td className="text-center px-3 py-2">
                      <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${p.plan === "premium" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"}`}>
                        {p.plan?.toUpperCase() || "\u2014"}
                      </span>
                    </td>
                    <td className="text-center px-3 py-2 text-slate-500">{p.billing || "\u2014"}</td>
                    <td className="text-center px-3 py-2 font-bold text-emerald-700">{"\u20AC"}{(p.amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-slate-400">{p.date ? new Date(p.date).toLocaleDateString("de-DE") : "\u2014"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        </div>
      </div>
    </Layout>
  );
}
